import React, {
  useEffect,
  useState,
} from "react";
import {
  EnvelopeOpen,
  FileText,
  MagnifyingGlass,
  PencilSimple,
  Trash,
} from "@phosphor-icons/react";
import {
  useSearchParams,
} from "react-router-dom";
import { toast } from "sonner";

import {
  mail as mailApi,
} from "@/api";
import ActaModal from "@/components/ui/ActaModal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";


function formatDate(value) {
  if (!value) return "—";

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return date.toLocaleString(
    "fr-FR",
    {
      dateStyle: "short",
      timeStyle: "short",
    }
  );
}


export default function MailDraftList() {
  const [, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refreshToken, setRefreshToken] = useState(0);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);

    mailApi
      .listDrafts({
        page,
        limit: 50,
        search,
      })
      .then((response) => {
        if (cancelled) return;

        setItems(response?.items || []);
        setTotalPages(
          Math.max(
            1,
            Number(response?.totalPages || 1)
          )
        );
      })
      .catch((error) => {
        if (!cancelled) {
          toast.error(
            error?.response?.data?.detail
            || "Impossible de charger les brouillons"
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [page, refreshToken, search]);

  function resumeDraft(draft) {
    window.sessionStorage.setItem(
      "acta-mail-draft",
      JSON.stringify({
        id: draft.id,
        to: draft.to || "",
        cc: draft.cc || "",
        bcc: draft.bcc || "",
        subject: draft.subject || "",
        body: draft.body || "",
        prospectId: draft.prospectId || "",
        inReplyTo: draft.inReplyTo || "",
        references: draft.references || "",
        files: [],
      })
    );

    setSearchParams({
      restoreDraft: "1",
    });
  }

  async function confirmDeleteDraft() {
    if (!draftToDelete?.id || deleting) return;

    setDeleting(true);

    try {
      await mailApi.deleteDraft(draftToDelete.id);
      const shouldGoBack = items.length === 1 && page > 1;

      setDraftToDelete(null);
      toast.success("Brouillon supprimé");

      if (shouldGoBack) {
        setPage((current) => Math.max(1, current - 1));
      } else {
        setRefreshToken((current) => current + 1);
      }
    } catch (error) {
      toast.error(
        error?.response?.data?.detail
        || "Suppression du brouillon impossible"
      );
    } finally {
      setDeleting(false);
    }
  }

  return (
    <>
      <Card
        variant="acta"
        className="min-h-0 min-w-0 overflow-hidden"
        data-testid="mail-drafts-list"
      >
        <div className="flex flex-col gap-3 border-b border-[var(--do-border)] p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <FileText size={20} weight="duotone" />
              <h2 className="font-heading text-lg font-black text-[var(--do-ink)]">
                Brouillons
              </h2>
            </div>
            <p className="mt-1 text-xs text-[var(--do-muted)]">
              Sélectionnez un brouillon pour reprendre sa rédaction.
            </p>
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="actaSecondary"
              onClick={() => setSearchParams({ folder: "inbox" })}
            >
              <EnvelopeOpen size={17} />
              Boîte de réception
            </Button>

            <Button
              type="button"
              variant="actaPrimary"
              onClick={() => setSearchParams({ compose: "1" })}
            >
              <PencilSimple size={17} />
              Nouveau mail
            </Button>
          </div>
        </div>

        <div className="border-b border-[var(--do-border)] p-4">
          <label className="flex items-center gap-2 rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-solid)] px-3">
            <MagnifyingGlass size={16} />
            <Input
              value={search}
              onChange={(event) => {
                setPage(1);
                setSearch(event.target.value);
              }}
              placeholder="Rechercher un brouillon"
              className="min-h-[40px] min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0"
            />
          </label>
        </div>

        <div className="min-h-0 min-w-0">
          {loading && (
            <div className="p-5 text-sm text-[var(--do-muted)]">
              Chargement…
            </div>
          )}

          {!loading && items.length === 0 && (
            <div className="p-8 text-center text-sm text-[var(--do-muted)]">
              Aucun brouillon.
            </div>
          )}

          {!loading && items.map((draft) => (
            <div
              key={draft.id}
              className="flex min-h-[82px] items-stretch border-b border-[var(--do-border)] transition hover:bg-[var(--do-bg-2)] last:border-b-0"
              data-testid="mail-draft-row"
            >
              <button
                type="button"
                onClick={() => resumeDraft(draft)}
                className="min-w-0 flex-1 px-4 py-3 text-left"
                data-testid="mail-draft-open"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-black text-[var(--do-ink)]">
                      {draft.to || "Sans destinataire"}
                    </div>
                    <div className="mt-1 truncate text-sm text-[var(--do-ink)]">
                      {draft.subject || "(sans objet)"}
                    </div>
                    <div className="mt-1 truncate text-xs text-[var(--do-muted)]">
                      {draft.body || ""}
                    </div>
                  </div>

                  <time className="shrink-0 text-[11px] text-[var(--do-muted)]">
                    {formatDate(draft.updatedAt)}
                  </time>
                </div>
              </button>

              <button
                type="button"
                onClick={() => setDraftToDelete(draft)}
                className="flex w-12 shrink-0 items-center justify-center border-l border-[var(--do-border)] text-[var(--do-muted)] transition hover:bg-[var(--do-bg-solid)] hover:text-[var(--do-rose)]"
                aria-label={`Supprimer le brouillon ${draft.subject || "sans objet"}`}
                data-testid="mail-draft-delete"
              >
                <Trash size={18} />
              </button>
            </div>
          ))}

          {!loading && totalPages > 1 && (
            <div className="flex items-center justify-between gap-3 border-t border-[var(--do-border)] p-3">
              <Button
                type="button"
                variant="actaSecondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Précédent
              </Button>

              <span className="text-xs font-bold text-[var(--do-muted)]">
                Page {page} / {totalPages}
              </span>

              <Button
                type="button"
                variant="actaSecondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Suivant
              </Button>
            </div>
          )}
        </div>
      </Card>

      <ActaModal
        open={Boolean(draftToDelete)}
        onClose={deleting ? undefined : () => setDraftToDelete(null)}
        title="Supprimer le brouillon"
      >
        <div
          className="min-w-0 space-y-4"
          data-testid="mail-draft-delete-confirmation"
        >
          <p className="text-sm text-[var(--do-ink)]">
            Voulez-vous supprimer définitivement ce brouillon ? Cette action est irréversible.
          </p>

          {draftToDelete && (
            <div className="rounded-xl border border-[var(--do-border)] bg-[var(--do-bg-2)] p-3">
              <div className="truncate text-sm font-black text-[var(--do-ink)]">
                {draftToDelete.subject || "(sans objet)"}
              </div>
              <div className="mt-1 truncate text-xs text-[var(--do-muted)]">
                {draftToDelete.to || "Sans destinataire"}
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="actaSecondary"
              onClick={() => setDraftToDelete(null)}
              disabled={deleting}
            >
              Annuler
            </Button>

            <Button
              type="button"
              variant="actaDanger"
              onClick={confirmDeleteDraft}
              disabled={deleting}
              data-testid="mail-draft-delete-confirm"
            >
              <Trash size={17} />
              {deleting ? "Suppression…" : "Supprimer"}
            </Button>
          </div>
        </div>
      </ActaModal>
    </>
  );
}
